/* istanbul ignore file */

import {HttpClient, HttpHandler, HttpInterceptor} from '@angular/common/http'
import {Inject, Injectable, Injector, Optional} from '@angular/core'
import {API_END_POINT_INTERCEPTOR, AUTH_TOKEN_INTERCEPTOR, ERROR_HANDLER_INTERCEPTOR, HTTP_DYNAMIC_INTERCEPTORS} from '../../shared/consts'
import {ApiEndPointInterceptor} from './interceptors/api-end-point.interceptor'
import {AuthTokenInterceptor} from './interceptors/auth-token.interceptor'
import {ErrorHandlerInterceptor} from './interceptors/error-handler.interceptor'
import {HttpInterceptorHandler} from './interceptors/http-interceptor-handler'
import {Interceptors} from './interceptors/interceptors.interface'

interface ServerError {
  [index: string]: any
}

/**
 * Note: Extends HttpClient with per request configuration using dynamic interceptors.
 */
@Injectable()
export class HttpService extends HttpClient {
  constructor(
    private httpHandler: HttpHandler,
    private injector: Injector,
    @Inject(HTTP_DYNAMIC_INTERCEPTORS) @Optional() private interceptors: Interceptors = {}
  ) {
    super(httpHandler)

    if (!this.interceptors) {
      this.configureDefaultInterceptors()
    }
  }

  skipErrorHandler(): HttpService {
    return this.removeInterceptor(ERROR_HANDLER_INTERCEPTOR)
  }

  disableApiPrefix(): HttpService {
    return this.removeInterceptor(API_END_POINT_INTERCEPTOR)
  }

  /**
   * Note: Override the original method to wire interceptors when triggering the request.
   * @param method (CRUD)
   * @param url Absolute URL to API method
   * @param options Request options (contains headers, auth, etc.)
   * @returns {any} Server response
   */
  request(method?: any, url?: string, options?: any): any {
    const handler = this.mapInterceptorsToArray().reduceRight(
      (next, interceptor) => new HttpInterceptorHandler(next, interceptor),
      this.httpHandler
    )
    return new HttpClient(handler).request(method, url, options)
  }

  get(url: string, options?: any): any {
    return new Promise((resolve, reject) => {
      super.get(url, options)
        .subscribe((data: any) => {
          resolve(data)
        }, (e: ServerError) => {
          reject(e)
        })
    })
  }

  post(url: string, body?: any, options?: any): any {
    return new Promise((resolve, reject) => {
      super.post(url, body, options)
        .subscribe((data: any) => {
          resolve(data)
        }, (e: ServerError) => {
          reject(e)
        })
    })
  }

  put(url: string, body?: any, options?: any): any {
    return new Promise((resolve, reject) => {
      super.put(url, body, options)
        .subscribe((data: any) => {
          resolve(data)
        }, (e: ServerError) => {
          reject(e)
        })
    })
  }

  delete(url: string, options?: any): any {
    return new Promise((resolve, reject) => {
      super.delete(url, options)
        .subscribe((data: any) => {
          resolve(data)
        }, (e: ServerError) => {
          reject(e)
        })
    })
  }

  setCredentials(credentials: {token: string, token_type: string}) {
    const authTokenInterceptor = this.getInterceptor(AUTH_TOKEN_INTERCEPTOR) as AuthTokenInterceptor
    authTokenInterceptor.setCredentials(credentials)
  }

  removeCredentials() {
    const authTokenInterceptor = this.getInterceptor(AUTH_TOKEN_INTERCEPTOR) as AuthTokenInterceptor
    authTokenInterceptor.removeCredentials()
  }

  /**
   * Note: Configure default interceptors that can be disabled here
   */
  private configureDefaultInterceptors() {
    this.interceptors = {
      [API_END_POINT_INTERCEPTOR]: this.injector.get(ApiEndPointInterceptor),
      [ERROR_HANDLER_INTERCEPTOR]: this.injector.get(ErrorHandlerInterceptor),
      [AUTH_TOKEN_INTERCEPTOR]: this.injector.get(AuthTokenInterceptor)
    }
  }

  private removeInterceptor(interceptorKey: string): HttpService {
    const copyOfInterceptors = {...this.interceptors}
    delete copyOfInterceptors[interceptorKey]
    return new HttpService(
      this.httpHandler,
      this.injector,
      copyOfInterceptors
    )
  }

  private addInterceptor(interceptor: HttpInterceptor): HttpService {
    return new HttpService(
      this.httpHandler,
      this.injector,
      {...this.interceptors, interceptor}
    ) as any
  }

  private mapInterceptorsToArray() {
    const interceptors: HttpInterceptor[] = []
    for (const key in this.interceptors) {
      if (this.interceptors.hasOwnProperty(key)) {
        interceptors.push(this.interceptors[key])
      }
    }
    return interceptors
  }

  private getInterceptor(key: string): HttpInterceptor {
    return this.interceptors[key]
  }

}
